/**
 * 슈퍼관리자 대시보드 — 서버 집계가 실패한 숫자(「집계 지연」) 표시 규칙.
 *
 * 서버(superAdminDashboard.service)는 계산이 실패하면
 *   - 일부 숫자 필드를 빈 값(null)으로 보내고,
 *   - 같은 객체에 `degraded: string[]` 를 붙인다(실패했을 때만 — 정상 응답에는 키 자체가 없다).
 *
 * 화면은 빈 값을 0 으로 그리면 안 된다(「회원 0명」은 실제 0명으로 읽힌다).
 * 🔴 옛 서버(빈 값·degraded 없음)에서는 지금과 똑같이 보여야 하므로,
 *    빈 값을 살리는 건 옛 서버가 null 을 보내지 않던 필드뿐이다.
 *    평균 단가(avgPricePerClass)는 옛 서버도 「PT 수업 없음」일 때 null 을 보냈고 화면은 0(「-」)으로 그렸다 —
 *    그래서 degraded 에 avgPrice 가 있을 때만 빈 값으로 둔다.
 */

export const DEGRADED_LABEL = '집계 지연';
export const MISSING_VALUE_TEXT = '—';

/** degraded 항목 → 영향받는 화면 필드. 'brandScope'(다른 회사 연결 판정 실패)는 브랜드 범위로 다시 센 숫자들에 표시한다 */
const DEGRADED_KEYS_BY_FIELD: Record<string, readonly string[]> = {
    totalMembers: ['totalMembers', 'brandScope'],
    sameDayCancelCount: ['sameDay', 'brandScope'],
    sameDayChangeCount: ['sameDay', 'brandScope'],
    avgPricePerClass: ['avgPrice', 'brandScope'],
    undeliveredRevenue: ['undeliveredRevenue'],
    totalClasses: ['classes'],
    noShowCount: ['classes'],
    journalCount: ['classes'],
    journalWriteRate: ['classes'],
};

/**
 * 서버가 빈 값으로 보낼 수 있는 필드 — 정렬에서 빈 값을 맨 뒤로 보내는 대상.
 * 수업 수·노쇼·일지 수는 강사 행만(다른 회사에도 연결된 강사의 미기재 일정 보정이 실패하면 null + 'classes').
 * 일지 작성률은 옛 서버도 null 을 보냈으므로 넣지 않는다(옛 정렬 유지).
 */
export const NULLABLE_DEGRADED_FIELDS: ReadonlySet<string> = new Set([
    'totalMembers',
    'sameDayCancelCount',
    'sameDayChangeCount',
    'avgPricePerClass',
    'totalClasses',
    'noShowCount',
    'journalCount',
]);

/** 응답 객체의 degraded 배열(없거나 모양이 틀리면 빈 배열) */
export const readDegraded = (source: unknown): string[] => {
    const raw = (source as { degraded?: unknown } | null | undefined)?.degraded;
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
};

/** 이 필드가 집계 지연 상태인가 */
export const isFieldDegraded = (degraded: readonly string[] | undefined | null, field: string): boolean => {
    if (!degraded || degraded.length === 0) return false;
    const keys = DEGRADED_KEYS_BY_FIELD[field];
    return !!keys && keys.some((k) => degraded.includes(k));
};

/**
 * 회원 수·당일 취소/변경·(강사) 수업 수·노쇼·일지 수 — 빈 값(null)은 빈 값으로, 그 외는 기존 `v || 0` 과 같다.
 * 옛 서버는 이 필드에 null 을 보내지 않으므로 옛 응답의 결과는 그대로다.
 */
export const countOrMissing = (value: unknown): number | null => (value === null ? null : (value as number) || 0);

/**
 * 평균 회당 단가 — degraded 에 avgPrice 가 있을 때만 빈 값을 살린다.
 * 그 외의 null(PT 수업 없음 — 옛 서버도 보내던 정상 빈 값)은 기존처럼 0.
 */
export const avgPriceOrMissing = (value: unknown, degraded: readonly string[]): number | null =>
    value === null && degraded.includes('avgPrice') ? null : (value as number) || 0;
